import { defineStore } from 'pinia';
import { useUpdateStatusStore } from './updateStatus';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, Ref } from 'vue';
import { IEntryDTO, IEntryFieldDTO, IEntrySectionDTO } from '@/dtos';
import { getEntryByYearAPI } from '@/services/entry.service';
import {
  createEntryForYearAPI,
  createFieldAPI,
  createSectionAPI,
  deleteFieldAPI,
  deleteSectionAPI,
  updateEntryAPI,
  updateFieldsAPI,
  updateSectionsAPI,
} from '@/services/admin';

export const useEntryStore = defineStore('entry', () => {
  const statusStore = useUpdateStatusStore();
  const queryClient = useQueryClient();

  const getEntryByYearQuery = (yearId: Ref<string>) => {
    return useQuery({
      queryKey: computed(() => ['entry-form', yearId.value]),
      queryFn: async (): Promise<IEntryDTO> => {
        const ret = await getEntryByYearAPI(yearId.value);
        ret.sections.sort((a, b) => a.order - b.order);
        ret.sections.forEach((section) => {
          section.fields.sort((a, b) => a.order - b.order);
        });
        return ret;
      },
      enabled: computed(() => !!yearId.value),
    });
  };

  // Mutations
  const createEntryForYear = useMutation({
    mutationFn: (yearId: string) => {
      return createEntryForYearAPI(yearId);
    },
    onSuccess: (_data, yearId) => {
      queryClient.invalidateQueries({ queryKey: ['entry-form', yearId] });
      statusStore.setSuccessStatus('entry-create', 'success:create');
    },
    onError: (err: string) => {
      statusStore.setErrorStatus(`entry-create`, err);
    },
  });

  const updateEntry = useMutation({
    mutationFn: (entry: IEntryDTO) => {
      return updateEntryAPI(entry);
    },
    onSuccess: (_data, entry: IEntryDTO) => {
      queryClient.invalidateQueries({ queryKey: ['entry-form'] });
      statusStore.setSuccessStatus(`entry-${entry.id}`, 'success:update');
    },
    onError: (err: string, entry: IEntryDTO) => {
      statusStore.setErrorStatus(`entry-${entry.id}`, err);
    },
  });

  const createSection = useMutation({
    mutationFn: ({ section }: { section: IEntrySectionDTO; yearId: string }) => {
      return createSectionAPI(section);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['entry-form', variables.yearId] });
      statusStore.setSuccessStatus('entry-section-create', 'success:create');
    },
    onError: (err: string) => {
      statusStore.setErrorStatus(`entry-section-create`, err);
    },
  });

  const updateSections = useMutation({
    mutationFn: ({
      sections,
      entryId,
    }: {
      sections: IEntrySectionDTO[];
      entryId: string;
      yearId: string;
    }) => {
      return updateSectionsAPI(entryId, sections);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['entry-form', variables.yearId] });
      statusStore.setSuccessStatus(`entry-section-update-${variables.entryId}`, 'success:update');
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`entry-section-update-${variables.entryId}`, err);
    },
  });

  const updateSection = useMutation({
    mutationFn: ({
      section,
      entryId,
    }: {
      section: IEntrySectionDTO;
      entryId: string;
      yearId: string;
    }) => {
      let sections = [];
      sections.push(section);
      return updateSectionsAPI(entryId, sections);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['entry-form', variables.yearId] });
      statusStore.setSuccessStatus(
        `entry-section-update-${variables.section.id}`,
        'success:update',
      );
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`entry-section-update-${variables.section.id}`, err);
    },
  });

  const removeSection = useMutation({
    mutationFn: ({ section }: { section: IEntrySectionDTO; yearId: string }) => {
      return deleteSectionAPI(section);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['entry-form', variables.yearId] });
      statusStore.setSuccessStatus(
        `entry-section-update-${variables.section.id}`,
        'success:delete',
      );
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`entry-section-update-${variables.section.id}`, err);
    },
  });

  const createField = useMutation({
    mutationFn: ({ field }: { field: IEntryFieldDTO; yearId: string }) => {
      return createFieldAPI(field);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['entry-form', variables.yearId] });
      statusStore.setSuccessStatus('entry-field-create', 'success:create');
    },
    onError: (err: string) => {
      statusStore.setErrorStatus(`entry-field-create`, err);
    },
  });

  const updateField = useMutation({
    mutationFn: ({
      field,
      entryId,
      sectionId,
    }: {
      field: IEntryFieldDTO;
      entryId: string;
      sectionId: string;
      yearId: string;
    }) => {
      let fields: IEntryFieldDTO[] = [];
      fields.push(field);
      return updateFieldsAPI(entryId, sectionId, fields);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['entry-form', variables.yearId] });
      statusStore.setSuccessStatus(`entry-field-update-${variables.field.id}`, 'success:update');
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`entry-field-update-${variables.field.id}`, err);
    },
  });

  const removeField = useMutation({
    mutationFn: ({ field }: { field: IEntryFieldDTO; yearId: string }) => {
      return deleteFieldAPI(field);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['entry-form', variables.yearId] });
      statusStore.setSuccessStatus(`entry-field-update-${variables.field.id}`, 'success:delete');
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`entry-field-update-${variables.field.id}`, err);
    },
  });

  const updateFields = useMutation({
    mutationFn: ({
      fields,
      entryId,
      sectionId,
    }: {
      fields: IEntryFieldDTO[];
      entryId: string;
      sectionId: string;
      yearId: string;
    }) => {
      return updateFieldsAPI(entryId, sectionId, fields);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['entry-form', variables.yearId] });
      statusStore.setSuccessStatus(`entry-field-update-${variables.sectionId}`, 'success:update');
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`entry-fields-update-${variables.sectionId}`, err);
    },
  });

  return {
    getEntryByYearQuery,
    createEntryForYear,
    createSection,
    updateSection,
    updateSections,
    removeSection,
    updateEntry,
    createField,
    updateField,
    removeField,
    updateFields,
  };
});
